import { useState } from 'react'
import { CircularProgress, DialogActions, DialogContent, Typography } from '@mui/material'
import { useDispatch } from 'react-redux'
import { DarkButton } from '@components'
import { useDatasetQueryContext } from '@datasets/dataset-query-context/hooks'
import { showSnackbar } from '@global/snackbar/snackbar.actions'
import { useApprovalContext, useCommentsContext } from '@templates/approval/contexts'
import { ActionType } from '@templates/approval/contexts/approvalContext/types'
import { api } from 'api'
import * as css from './styles'
import { t } from '@lingui/core/macro'
import { AxiosError } from 'axios'

function getErrorMessageForSaveComments(status?: number) {
  const errorMessage = t`Failed to save comments`

  if (status) {
    if (status >= 500) {
      return `${errorMessage}: ${t`Server error.`}`
    }

    if (status >= 400) {
      return `${errorMessage}: ${t`You must be an approver to comment and reject.`}`
    }
  }

  return errorMessage
}

function getErrorMessageForRejectSubmission(errorDetails?: string) {
  const errorMessage = t`Failed to reject submission`

  if (errorDetails) {
    return `${errorMessage}: ${errorDetails}`
  }

  return errorMessage
}
interface CommentDTO {
  cvId: string
  fieldId: number
  comment: string
}

interface CommentsDTO {
  commitRef: string
  comments: CommentDTO[]
}

interface Props {
  handleClose: () => void
}

export const ConfirmRejectDialogContent = ({ handleClose }: Props) => {
  const reduxDispatch = useDispatch()
  const datasetQueryContext = useDatasetQueryContext()
  const { dispatch, showApprovalRequestsTable, reviewingRequest } = useApprovalContext() ?? {
    dispatch: () => void {},
    showApprovalRequestsTable: () => void {},
    reviewingRequest: null,
  }
  const { comments } = useCommentsContext()
  const [rejecting, setRejecting] = useState(false)

  return (
    <>
      <DialogContent css={css.content}>
        <Typography variant="body1">{t`You are about to reject this submission`}</Typography>
      </DialogContent>
      <DialogActions>
        <DarkButton onClick={handleClose}>{t`Cancel`}</DarkButton>
        <css.ButtonContainer>
          <DarkButton onClick={handleConfirm} disabled={rejecting} contained>
            {t`Confirm`}
          </DarkButton>
          {rejecting && <CircularProgress size={20} css={css.buttonProgress} />}
        </css.ButtonContainer>
      </DialogActions>
    </>
  )

  async function handleConfirm() {
    setRejecting(true)

    try {
      await saveComments()
    } catch (e) {
      let errorMessage = e instanceof Error ? e.message : t`Unknown error`
      if (e instanceof AxiosError) {
        errorMessage = getErrorMessageForSaveComments(e.status)
      }
      setRejecting(false)
      handleClose()
      reduxDispatch(
        showSnackbar({
          message: errorMessage,
          type: 'ERROR',
          duration: 5000,
        }),
      )
      return
    }

    try {
      await rejectSubmission()
      setRejecting(false)
      dispatch({
        type: ActionType.setReviewingRequest,
        payload: {
          reviewingRequest: null,
          reloadApprovalRequests: true,
        },
      })
      reduxDispatch(
        showSnackbar({
          message: t`Submission rejected successfully`,
          type: 'SUCCESS',
          duration: 5000,
        }),
      )
      showApprovalRequestsTable()
    } catch (e) {
      let errorMessage: string | undefined
      if (e instanceof AxiosError) {
        errorMessage = e.response?.data?.error
      }
      errorMessage = getErrorMessageForRejectSubmission(errorMessage)
      setRejecting(false)
      handleClose()
      reduxDispatch(
        showSnackbar({
          message: errorMessage,
          type: 'ERROR',
          duration: 5000,
        }),
      )
    }
  }

  function rejectSubmission() {
    const submissionId = String(reviewingRequest?.submission_id ?? -1)
    return api.put(`/api/v1/account/dataset/submission/${submissionId}/reject`)
  }

  function saveComments() {
    const commentsDTO = generateCommentsDTO()
    const payload: CommentsDTO = {
      commitRef:
        typeof datasetQueryContext.commitRef === 'string' ? datasetQueryContext.commitRef : '',
      comments: commentsDTO,
    }
    const submissionId = String(reviewingRequest?.submission_id ?? -1)
    return api.post(`/api/core/v1/account/dataset/submission/${submissionId}/comments`, payload)
  }

  function generateCommentsDTO(): CommentDTO[] {
    return Object.entries(comments).flatMap(([cvId, comment]) =>
      Object.entries(comment).map(([fieldId, field]) => ({
        cvId,
        fieldId: Number(fieldId),
        comment: field.message,
      })),
    )
  }
}
